'use es6';

import { toObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/toObjectSegFilter';
import Known from 'customer-data-filters/filterQueryFormat/operator/Known';
import I18n from 'I18n';
import { Small } from 'foundations-components/transitional/components/Small';
import { isEmptySnowflakeProperty } from '../../../../metadata/report-meta';
import { Filter } from '../../../../schema/filter-records';
import { createFieldFromFilterRef, isRelativeTimeProperty } from '../../../../utils/column-utils';
import { SNOWFLAKE_FILTER_FAMILY, toDSFieldRecord } from '../../xo-filter-utils';
import { useFilterEdit } from '../FilterEditContext';
import SnowflakeFilterEditPopover from '../SnowflakeFilterEditPopover';
import SnowflakeFilterTile from '../SnowflakeFilterTile';
import SnowflakeFilterWrapper from '../SnowflakeFilterWrapper';
import { useGroupIndices, useProperty, useTableSource } from '../SourceMeta';
import DeletedStagedPropertyFilterTile from './DeletedStagedPropertyFilterTile';
import InvalidStagedPropertyFilterTile from './InvalidStagedPropertyFilterTile';
import PropertyFilterLabel from './PropertyFilterLabel';
import { getIsUngated, useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';
import { NewPropertyFilterPanel } from './NewPropertyFilterPanel';
import { getIsUngatedToSnowflakePropertyFilterMigration } from '../../../../utils/filter-utils';
import PropertyFilterPanelBase from './PropertyFilterPanel';
import { MIGRATE_LEGACY_ILS_DATE_TIME_FILTERS, RTP_FILTERS } from 'reporting-data/constants/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StagedPropertyFilterTile = ({
  filterRef,
  index
}) => {
  const {
    table,
    property: name
  } = filterRef;
  const [{
    editingStagedFilterIndex
  }, editCallbacks] = useFilterEdit();
  const {
    userGates
  } = useFilterRelevantUserInfo();
  const isUngatedToSnowflakePropertyFilters = getIsUngatedToSnowflakePropertyFilterMigration(userGates);
  const isUngatedToTimePointMigration = getIsUngated(MIGRATE_LEGACY_ILS_DATE_TIME_FILTERS, userGates);
  const isUngatedToRtpFilters = getIsUngated(RTP_FILTERS, userGates);
  const FilterPanel = isUngatedToSnowflakePropertyFilters ? NewPropertyFilterPanel : PropertyFilterPanelBase;
  const groupIndices = useGroupIndices();
  const source = useTableSource(table);
  const missingSource = source == null;
  const property = useProperty(table, name);
  const missingProperty = property == null;
  const handleRemove = () => {
    editCallbacks.handleRemoveStagedFilter(index);
  };
  if (missingSource || missingProperty || isEmptySnowflakeProperty(property)) {
    return /*#__PURE__*/_jsx(DeletedStagedPropertyFilterTile, {
      filterRef: filterRef,
      onRemove: handleRemove
    });
  }
  const propertyIsRelativeTime = isRelativeTimeProperty(property);
  if (propertyIsRelativeTime && !(isUngatedToTimePointMigration && isUngatedToSnowflakePropertyFilters && isUngatedToRtpFilters)) {
    return /*#__PURE__*/_jsx(SnowflakeFilterWrapper, {
      highlighted: false,
      onRemove: () => handleRemove,
      readOnly: true,
      children: /*#__PURE__*/_jsx(InvalidStagedPropertyFilterTile, {
        filterRef: filterRef,
        onRemove: handleRemove
      })
    });
  }
  const field = createFieldFromFilterRef(filterRef, property);
  const filter = Filter({
    field,
    filter: {
      filterType: 'PROPERTY',
      property: name,
      operation: undefined
    }
  });
  const editableFilter = Filter({
    field,
    filter: toObjectSegFilterStrict(Known.of(toDSFieldRecord(property, source, userGates)), SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true)
  });
  const showEditPanel = index === editingStagedFilterIndex;
  const handleEdit = () => {
    editCallbacks.handleStagedFilterEdit(index);
  };
  const editPanel = /*#__PURE__*/_jsx(FilterPanel, {
    filter: editableFilter,
    groupWithIds: groupIndices.add(undefined),
    onDone: ({
      draftFilter,
      draftGroupId
    }) => editCallbacks.handleStagedFilterChange(draftFilter, draftGroupId),
    onCancel: editCallbacks.handleCancelEdit
  });
  return /*#__PURE__*/_jsx(SnowflakeFilterTile, {
    children: /*#__PURE__*/_jsx(SnowflakeFilterEditPopover, {
      editPanel: editPanel,
      open: showEditPanel,
      onClose: editCallbacks.handleCancelEdit,
      children: /*#__PURE__*/_jsxs(SnowflakeFilterWrapper, {
        highlighted: showEditPanel,
        onEdit: handleEdit,
        onRemove: handleRemove,
        children: [/*#__PURE__*/_jsx(PropertyFilterLabel, {
          filter: filter
        }), /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: I18n.text('reporting-snowflake.filter-editor.click-to-apply')
        })]
      })
    })
  });
};
export default StagedPropertyFilterTile;